import * as React from "react";
import { ReactNode, Children, isValidElement, cloneElement } from "react";
import { Helmet } from "react-helmet-async";
import { languages, useI18n } from "@/i18n";
import { localizedHead } from "@/i18n/headTranslations";
import Navigation from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { RelatedArticles } from "@/components/blog/RelatedArticles";
import SocialShare from "./SocialShare";
import { Button } from "@/components/ui/button";
import { LocalizedLink as Link } from "@/components/LocalizedLink";
import { ArrowLeft, Calendar, Clock, User, Tag, CheckCircle } from "lucide-react";
import { InternalLinkInjector } from "@/components/InternalLinkInjector";
import { QuickAnswer } from "@/components/seo/QuickAnswer";
import { FAQSection, type FAQItem } from "@/components/seo/FAQSection";

interface BlogPostTemplateProps {
  title: string;
  metaTitle: string;
  metaDescription: string;
  keywords: string;
  author: string;
  date: string;
  readTime: string;
  category: string;
  tags: string[];
  featuredImage: string;
  featuredImageAlt: string;
  children: ReactNode;
  relatedProducts?: string[];
  /** Optional custom TL;DR text. Defaults to metaDescription. */
  quickAnswer?: ReactNode;
  /** Optional custom FAQ items. Defaults to 4 auto-generated questions based on title/category. */
  faqs?: FAQItem[];
}

/**
 * Recursively processes children to inject internal links into text nodes
 */
const BlogContentWithLinks = ({ children, currentUrl }: { children: ReactNode; currentUrl: string }) => {
  const processNode = (node: ReactNode): ReactNode => {
    // Handle string content - inject links
    if (typeof node === 'string') {
      return (
        <InternalLinkInjector 
          maxLinks={2} 
          excludeUrls={[currentUrl]}
        >
          {node}
        </InternalLinkInjector>
      );
    }
    
    // Handle arrays
    if (Array.isArray(node)) {
      return node.map((child, index) => (
        <React.Fragment key={index}>{processNode(child)}</React.Fragment>
      ));
    }
    
    // Handle React elements - recursively process children
    if (isValidElement(node)) {
      const element = node as React.ReactElement;
      // Don't process Link, a, or code elements to avoid nesting links
      if (
        element.type === Link || 
        element.type === 'a' || 
        element.type === 'code' || 
        element.type === 'pre'
      ) {
        return node;
      }
      
      // Clone element with processed children
      return cloneElement(element, {
        ...element.props,
        children: processNode(element.props.children)
      });
    }
    
    return node;
  };

  return <>{processNode(children)}</>;
};

const BlogPostTemplate = ({
  title,
  metaTitle,
  metaDescription,
  keywords,
  author,
  date,
  readTime,
  category,
  tags,
  featuredImage,
  featuredImageAlt,
  children,
  relatedProducts = [],
  quickAnswer,
  faqs,
}: BlogPostTemplateProps) => {
  const SITE_ORIGIN = 'https://www.aureum7trading.com';
  const currentUrl =
    typeof window !== 'undefined'
      ? `${SITE_ORIGIN}${window.location.pathname}`
      : SITE_ORIGIN;
  const localePath =
    typeof window !== 'undefined'
      ? window.location.pathname.replace(/^\/([a-z]{2})(?=\/|$)/, '') || '/'
      : '/';
  const absoluteImage = featuredImage.startsWith('http')
    ? featuredImage
    : `${SITE_ORIGIN}${featuredImage.startsWith('/') ? '' : '/'}${featuredImage}`;
  const isoDate = (() => {
    const parsed = new Date(date);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString().slice(0, 10);
  })();
  const { language } = useI18n();
  const head = localizedHead(language, localePath, { title: metaTitle, description: metaDescription });

  // Smart default FAQs — parameterized by title & category so every post gets
  // relevant, non-fabricated FAQPage JSON-LD without per-file editing.
  const defaultFaqs: FAQItem[] = [
    {
      question: `What is this ${category.toLowerCase()} guide about?`,
      answer: `${metaDescription} This article — "${title}" — walks through the key points buyers, homeowners, and specifiers should understand before choosing a luxury smart toilet or bathroom fixture from Aureum VII Trading.`,
    },
    {
      question: "Which Aureum VII Trading smart toilet is the flagship?",
      answer: "The LUXA M80 is Aureum VII Trading's flagship luxury smart toilet — a tankless, floor-standing model with motion-sensor auto lid, heated bidet with adjustable pressure and temperature, warm-air dryer, UV self-cleaning nozzles, IPX4 protection and a 5-year warranty. It is available at €600 per unit and ships across the EU.",
    },
    {
      question: "What warranty and after-sales support come with Aureum VII smart toilets?",
      answer: "All smart toilet models ship with a 5-year comprehensive warranty covering electronics, heating and ceramic body. Aureum VII Trading provides EU-based after-sales support, spare parts, installation guides and CAD/BIM files for architects on request.",
    },
    {
      question: "How can I request a quote or book a consultation?",
      answer: "Contact Aureum VII Trading at info@aureum7trading.com or WhatsApp +34 642 278 939. Volume pricing is available from 20+ units for hotel, developer and OEM projects across Spain, France, Germany, Italy, the UK, Portugal, the Netherlands, Belgium and Austria.",
    },
  ];

  const resolvedFaqs = faqs && faqs.length > 0 ? faqs : defaultFaqs;


  // Generate structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": title,
    "image": absoluteImage,
    "author": {
      "@type": "Organization",
      "name": author
    },
    "publisher": {
      "@type": "Organization",
      "name": "Aureum7 Trading",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.aureum7trading.com/lovable-uploads/7a774286-73c5-43d4-b368-b47766cb1465.png"
      }
    },
    ...(isoDate ? { datePublished: isoDate, dateModified: isoDate } : {}),
    "description": metaDescription,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": currentUrl
    },
    "keywords": keywords,
    "articleSection": category
  };

  return (
    <>
      <Helmet>
        <title>{head.title}</title>
        <meta name="description" content={head.description} />
        <meta name="keywords" content={keywords} />
        <meta property="og:title" content={head.title} />
        <meta property="og:description" content={head.description} />
        <meta property="og:image" content={absoluteImage} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={currentUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={head.title} />
        <meta name="twitter:description" content={head.description} />
        <meta name="twitter:image" content={absoluteImage} />
        <meta property="article:author" content={author} />
        <meta property="article:published_time" content={isoDate ?? date} />
        <meta property="article:section" content={category} />
        {tags.map((tag) => (
          <meta key={tag} property="article:tag" content={tag} />
        ))}
        <link rel="canonical" href={currentUrl} />
        {languages.map((lang) => (
          <link
            key={lang.code}
            rel="alternate"
            hrefLang={lang.locale}
            href={`${SITE_ORIGIN}/${lang.code}${localePath}`}
          />
        ))}
        <link rel="alternate" hrefLang="x-default" href={`${SITE_ORIGIN}/en${localePath}`} />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen flex flex-col bg-background">
        <Navigation />
        
        <main className="flex-1">
          {/* Back to Blog */}
          <div className="container mx-auto px-4 pt-24 pb-6">
            <Link to="/blog">
              <Button variant="ghost" className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back to Blog
              </Button>
            </Link>
          </div>

          {/* Article Header */}
          <article className="container mx-auto px-4 pb-16 max-w-4xl">
            <header className="mb-8">
              {/* Category Badge */}
              <div className="mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary/10 text-primary">
                  {category}
                </span>
              </div>

              {/* Title */}
              <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                {title}
              </h1>

              {/* Meta Information */}
              <div className="flex flex-wrap items-center gap-4 text-muted-foreground mb-6">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  <span className="text-sm">{author}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <time className="text-sm">{date}</time>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  <span className="text-sm">{readTime}</span>
                </div>
              </div>

              {/* Social Share Buttons */}
              <SocialShare url={currentUrl} title={title} />
            </header>

            {/* Featured Image */}
            <div className="mb-8 rounded-lg overflow-hidden">
              <img
                src={featuredImage}
                alt={featuredImageAlt}
                className="w-full h-auto object-cover"
                loading="lazy"
              />
            </div>

            {/* TL;DR / Quick Answer — optimized for AI answer engines */}
            <QuickAnswer question="Quick Answer">
              {quickAnswer ?? metaDescription}
            </QuickAnswer>

            {/* Article Content with Automatic Internal Linking */}
            <div className="prose prose-lg max-w-none dark:prose-invert">
              <BlogContentWithLinks currentUrl={typeof window !== 'undefined' ? window.location.pathname : ''}>
                {children}
              </BlogContentWithLinks>
            </div>

            {/* FAQ Section — emits FAQPage JSON-LD for search & AI engines */}
            <div className="mt-12">
              <FAQSection
                heading="Frequently Asked Questions"
                faqs={resolvedFaqs}
                className="!py-0 !px-0"
              />
            </div>

            {/* Tags */}
            {tags.length > 0 && (
              <div className="mt-12 pt-8 border-t">
                <div className="flex items-center gap-2 flex-wrap">
                  <Tag className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium text-muted-foreground">Tags:</span>
                  {tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-muted text-foreground"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}


            {/* Internal Linking - Key Pages */}
            <div className="mt-12 p-6 bg-gradient-to-br from-primary/5 to-accent/5 rounded-lg border border-primary/10">
              <h3 className="text-xl font-semibold mb-4">Explore More</h3>
              <div className="grid md:grid-cols-2 gap-3">
                <Link to="/smart-toilets" className="flex items-center gap-2 text-sm hover:text-primary transition-colors">
                  <CheckCircle className="w-4 h-4" />
                  <span>Browse All Smart Toilet Collections</span>
                </Link>
                <Link to="/blog" className="flex items-center gap-2 text-sm hover:text-primary transition-colors">
                  <CheckCircle className="w-4 h-4" />
                  <span>Read More Buying Guides</span>
                </Link>
                <Link to="/sustainability" className="flex items-center gap-2 text-sm hover:text-primary transition-colors">
                  <CheckCircle className="w-4 h-4" />
                  <span>Eco-Friendly Bathroom Solutions</span>
                </Link>
                <Link to="/about" className="flex items-center gap-2 text-sm hover:text-primary transition-colors">
                  <CheckCircle className="w-4 h-4" />
                  <span>About Aureum VII Trading</span>
                </Link>
                <Link to="/business" className="flex items-center gap-2 text-sm hover:text-primary transition-colors">
                  <CheckCircle className="w-4 h-4" />
                  <span>B2B & Project Supply</span>
                </Link>
                <Link to="/bath-sanitary" className="flex items-center gap-2 text-sm hover:text-primary transition-colors">
                  <CheckCircle className="w-4 h-4" />
                  <span>Bath & Sanitary Collection</span>
                </Link>
                <Link to="/smart-toilet-cost" className="flex items-center gap-2 text-sm hover:text-primary transition-colors">
                  <CheckCircle className="w-4 h-4" />
                  <span>Smart Toilet Cost Guide</span>
                </Link>
              </div>
            </div>

            {/* Related Products CTA */}
            {relatedProducts.length > 0 && (
              <div className="mt-8 p-6 bg-muted rounded-lg">
                <h3 className="text-xl font-semibold mb-4">
                  Explore Our Premium Collection
                </h3>
                <p className="mb-4 text-muted-foreground">
                  Discover the smart toilet models featured in this article and find the perfect fit for your home.
                </p>
                <div className="flex gap-3 flex-wrap">
                  {relatedProducts.map((product) => (
                    <Link key={product} to={`/products/${product}`}>
                      <Button variant="default">
                        View {product.charAt(0).toUpperCase() + product.slice(1)} Series
                      </Button>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Share Again */}
            <div className="mt-12 pt-8 border-t">
              <p className="text-sm font-medium mb-4">Share this article:</p>
              <SocialShare url={currentUrl} title={title} />
            </div>
          </article>
        </main>

        {/* Related Articles Section */}
        <RelatedArticles 
          currentSlug={typeof window !== 'undefined' ? window.location.pathname : ''}
          className="mb-0"
        />

        {/* CTA Section */}
        <section className="py-16 px-6 lg:px-12 bg-gradient-to-br from-accent/5 via-primary/5 to-accent/5">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="font-playfair text-3xl sm:text-4xl font-bold mb-4 text-primary">
              Ready to Upgrade Your Bathroom?
            </h2>
            <p className="font-inter text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Discover our luxury smart toilet collections and experience the perfect blend of European elegance and intelligent technology.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
              <Button size="lg" asChild>
                <Link to="/smart-toilets">Browse Collections</Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="/contact">Schedule Consultation</Link>
              </Button>
            </div>
            <div className="pt-6 border-t border-border/20">
              <p className="text-sm text-muted-foreground mb-3">Contact us directly:</p>
              <div className="flex flex-wrap justify-center gap-6">
                <a 
                  href="mailto:info@aureum7trading.com" 
                  className="flex items-center gap-2 text-accent hover:text-accent/80 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span className="font-medium">info@aureum7trading.com</span>
                </a>
                <a 
                  href="tel:+34642278939" 
                  className="flex items-center gap-2 text-accent hover:text-accent/80 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span className="font-medium">+34 642 278 939</span>
                </a>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default BlogPostTemplate;
